import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Download, FileText, ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function SmartSofaPage() {
  const {
    t
  } = useLanguage();
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const advantages = [t('sofa.adv1'), t('sofa.adv2'), t('sofa.adv3'), t('sofa.adv4'), t('sofa.adv5'), t('sofa.adv6')];
  return <div className="w-full bg-white pb-32" data-id="element-830">
      <div className="bg-corporate-dark text-white py-24 md:py-40 px-4 relative overflow-hidden" data-id="element-831">
        <div className="absolute inset-0 opacity-20" data-id="element-832">
          <img src="https://cdn.magicpatterns.com/uploads/avDqfGk23uiBNW5qCPZ9R6/sofa_3.png" alt="Background" className="w-full h-full object-cover" data-id="element-833" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10 mt-10" data-id="element-834">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-835">
            <span className="text-accent-beige font-medium tracking-[0.2em] uppercase text-sm mb-6 block" data-id="element-836">
              {t('prod.line')}
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-6 tracking-tight" data-id="element-837">
              SmartSofa
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-2xl font-light leading-relaxed" data-id="element-838">
              {t('sofa.hero.desc')}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-28" data-id="element-839">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20" data-id="element-840">
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} data-id="element-841">
            <h2 className="text-4xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-842">
              {t('sofa.sec.title')}
            </h2>
            <div className="text-lg text-gray-600 font-light leading-relaxed space-y-6 mb-12" data-id="element-843">
              <p data-id="element-844">{t('sofa.sec.p1')}</p>
              <p data-id="element-845">{t('sofa.sec.p2')}</p>
            </div>

            <h3 className="text-2xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-846">
              {t('prod.adv.title')}
            </h3>
            <ul className="space-y-5 mb-12" data-id="element-847">
              {advantages.map((adv, idx) => <li key={idx} className="flex items-start" data-id="element-848">
                  <CheckCircle2 className="h-6 w-6 text-corporate-blue mr-4 flex-shrink-0 mt-0.5" data-id="element-849" />
                  <span className="text-gray-700 text-lg font-light" data-id="element-850">
                    {adv}
                  </span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="space-y-8" data-id="element-851">
            <div className="h-96 bg-gray-100 rounded-sm overflow-hidden group" data-id="element-852">
              <img src="https://cdn.magicpatterns.com/uploads/avDqfGk23uiBNW5qCPZ9R6/sofa_3.png" alt="SmartSofa" className="w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105" data-id="element-853" />
            </div>
            <div className="grid grid-cols-2 gap-8" data-id="element-854">
              <div className="bg-neutral-grey p-8 rounded-sm" data-id="element-855">
                <h4 className="text-xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-856">
                  {t('sofa.app1.title')}
                </h4>
                <p className="text-base text-gray-600 font-light leading-relaxed" data-id="element-857">
                  {t('sofa.app1.desc')}
                </p>
              </div>
              <div className="bg-neutral-grey p-8 rounded-sm" data-id="element-858">
                <h4 className="text-xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-859">
                  {t('sofa.app2.title')}
                </h4>
                <p className="text-base text-gray-600 font-light leading-relaxed" data-id="element-860">
                  {t('sofa.app2.desc')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Documentación SmartSofa */}
        <motion.div id="documentacion-smartsofa" initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="mt-24 lg:mt-32 scroll-mt-24" data-id="element-861">
          <h3 className="text-3xl font-heading font-bold text-corporate-dark mb-3 tracking-tight" data-id="element-862">
            {t('prod.docs.sofa.title')}
          </h3>
          <p className="text-lg text-gray-500 font-light mb-12 max-w-3xl" data-id="element-863">
            {t('prod.docs.sofa.desc')}
          </p>

          <div className="space-y-4" data-id="element-864">
            {[{
            id: 'catalogos',
            title: t('prod.docs.catalogs'),
            docs: [{
              name: t('sofa.cat1'),
              url: 'https://drive.google.com/file/d/1l6HV07j4u3_p7Lt_1rlBz8dNuVF7wBOh/view?usp=sharing'
            }]
          }].map(category => <div key={category.id} className="bg-white border border-gray-100 rounded-sm shadow-md overflow-hidden" data-id="element-865">
                <button onClick={() => setOpenAccordion(openAccordion === category.id ? null : category.id)} className="w-full flex items-center justify-between px-8 py-6 text-left hover:bg-neutral-grey transition-colors duration-300" data-id="element-866">
                  <div data-id="element-867">
                    <h4 className="text-xl font-heading font-bold text-corporate-dark tracking-tight" data-id="element-868">
                      {category.title}
                    </h4>
                    <p className="text-sm text-gray-500 font-light mt-1" data-id="element-869">
                      {category.docs.length}{' '}
                      {category.docs.length === 1 ? t('prod.docs.count.one') : t('prod.docs.count.many')}
                    </p>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-corporate-blue flex-shrink-0 ml-4 transition-transform duration-300 ${openAccordion === category.id ? 'rotate-180' : ''}`} data-id="element-870" />
                </button>
                <AnimatePresence initial={false} data-id="element-871">
                  {openAccordion === category.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.35,
                ease: [0.25, 0.1, 0.25, 1]
              }} className="overflow-hidden" data-id="element-872">
                      <div className="divide-y divide-gray-100 border-t border-gray-100" data-id="element-873">
                        {category.docs.map((doc, idx) => <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between px-8 py-5 hover:bg-neutral-grey transition-colors duration-300" data-id="element-874">
                            <div className="flex items-center mb-4 sm:mb-0" data-id="element-875">
                              <FileText className="h-5 w-5 text-corporate-blue mr-4 flex-shrink-0" data-id="element-876" />
                              <span className="text-base text-gray-800 font-medium" data-id="element-877">
                                {doc.name}
                              </span>
                            </div>
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-5 py-2.5 border border-corporate-blue text-corporate-blue hover:bg-corporate-blue hover:text-white transition-all duration-500 ease-premium rounded-md text-sm font-medium w-full sm:w-auto hover:scale-[1.02]" data-id="element-878">
                              <Download className="mr-2 h-4 w-4" data-id="element-879" />
                              {t('prod.docs.download')}
                            </a>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}